import { startCase, toLower } from 'lodash'
import Dinero from 'dinero.js'
import store from 'state/store'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision, unformatStringNumber } from 'utils/helpers'
import { PRICE_TYPES } from 'utils/constants'

export const formatInitialPriceListMaterialData = (priceEntries, defaultPricesDict) =>
  priceEntries
    .map(priceEntry => ({
      id: priceEntry.id,
      commodity: `${startCase(toLower(priceEntry.commodityName))}`,
      materialName: priceEntry.materialName,
      materialCode: priceEntry.materialCode,
      materialTypeId: priceEntry.materialTypeId,
      defaultBuyPrice: defaultPricesDict?.[priceEntry?.materialTypeId]?.defaultBuyPrice,
      commercialBuyPrice: setDecimalPrecision(Dinero({ ...priceEntry?.buyPrice?.price }).toUnit()),
      isFormula: priceEntry?.buyPrice?.priceType === 'FORMULA',
      defaultUm: defaultPricesDict?.[priceEntry?.materialTypeId]?.defaultUm,
      um: priceEntry.weightUnits,
      priceId: priceEntry?.buyPrice?.id,
    }))
    .sort((a, b) => {
      if (a.commodity < b.commodity) return -1
      if (a.commodity > b.commodity) return 1
      if (a.materialName < b.materialName) return -1
      if (a.materialName > b.materialName) return 1
      return 0
    })

export const formatSubmitPriceListMaterialData = (materials, priceSheetData, defaultCurrency) =>
  materials.map(material => {
    const decimalPrecisionSetting =
      store.getState()?.settings?.settings?.purchaseSettings?.inboundTickets?.decimalPrecision || 2

    const { commercialBuyPrice = 0 } = material
    const previousPriceEntry = priceSheetData?.entriesObj?.find(priceEntry => priceEntry.id === material.id)
    const formattedPriceEntry =
      material.isFormula && previousPriceEntry
        ? previousPriceEntry
        : {
            id: material.id,
            materialCode: material?.materialCode || material.code,
            materialTypeId: material.materialTypeId,
            priceSheetId: priceSheetData?.id,
            weightUnits: material.um,
            isNew: false,
            buyPrice: {
              id: material.priceId,
              isBuyPrice: true,
              priceType: 'SPOT',
              price: {
                amount: roundTo(commercialBuyPrice * 10 ** decimalPrecisionSetting),
                currency: defaultCurrency,
                precision: decimalPrecisionSetting,
              },
            },
          }
    return formattedPriceEntry
  })

export const checkIfPriceEntryIsEdited = (priceEntry, formData) => {
  const buyPriceIsEdited =
    !!formData?.buyPrice && priceEntry.buyPrice?.priceType === PRICE_TYPES.SPOT.id
      ? setDecimalPrecision(formData.buyPrice) !==
        setDecimalPrecision(Dinero({ ...priceEntry.buyPrice?.price }).toUnit())
      : false
  const minMaxBuyRangeIsEdited =
    !!formData?.minMaxBuyRange && priceEntry.buyPrice?.priceType === PRICE_TYPES.SPOT.id
      ? unformatStringNumber(formData.minMaxBuyRange) !== priceEntry.buyPrice?.minMaxRange
      : false
  const sellPriceIsEdited =
    !!formData?.sellPrice && priceEntry.sellPrice?.priceType === PRICE_TYPES.SPOT.id
      ? setDecimalPrecision(formData.sellPrice) !==
        setDecimalPrecision(Dinero({ ...priceEntry.sellPrice?.price }).toUnit())
      : false
  const minMaxSellRangeIsEdited =
    !!formData?.minMaxSellRange && priceEntry.sellPrice?.priceType === PRICE_TYPES.SPOT.id
      ? unformatStringNumber(formData.minMaxSellRange) !== priceEntry.sellPrice?.minMaxRange
      : false

  const state = store.getState()
  const { disableUmDropdowns } = state?.settings?.settings?.purchaseSettings?.inboundTickets || {}

  const unitsIsEdited = disableUmDropdowns ? false : formData?.weightUnits !== priceEntry.weightUnits

  return buyPriceIsEdited || minMaxBuyRangeIsEdited || sellPriceIsEdited || minMaxSellRangeIsEdited || unitsIsEdited
}
